/**
 * Module dependencies.
 */

import { Image, RouterLink, Type } from 'src/components/core';
import { ReactElement } from 'react';
import { theme as amtrolAlfaTheme } from 'src/styles/theme';
import { color, media, units } from '@untile/react-components';
import { prop } from 'styled-tools';
import { useRouter } from 'next/router';
import styled from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  bgColor: string;
  className?: string;
  image?: string;
  title: string;
  url: string;
};

/**
 * `StyledImage` styled component.
 */

const StyledImage = styled(Image)`
  transition: transform 0.3s ease;
  will-change: transform;
`;

/**
 * `Title` styled component.
 */

const Title = styled(Type.H3)`
  transition: transform 0.4s ease;
  will-change: transform;
`;

/**
 * `Card` styled component.
 */

const Card = styled(RouterLink)<{ bgColor: string }>`
  background-color: ${prop('bgColor')};
  color: ${color('grey800')};
  display: grid;
  grid-template-columns: 100%;
  grid-template-rows: 1fr max-content;
  height: 100%;
  outline: none;
  overflow: hidden;
  padding: ${units(2.5)} ${units(2.5)} ${units(4)};
  position: relative;
  text-decoration: none;
  transition: transform 0.3s ease;
  width: 100%;
  will-change: transform;

  &:focus,
  &:hover {
    transform: scale(0.98);

    ${StyledImage} {
      transform: scale(0.98);
    }

    ${Title} {
      transform: scale(0.965);
    }
  }

  ${media.min('md')`
    padding: ${units(5)} ${units(5)} ${units(10)};
    width: ${units(72.5)};
  `}
`;

/**
 * `ImageWrapper` styled component.
 */

const ImageWrapper = styled.div`
  overflow: hidden;
  position: relative;
`;

/**
 * `CarouselSquareCard` component.
 */

const CarouselSquareCard = (props: Props): ReactElement => {
  const { bgColor, className, image, title, url } = props;
  const { locale } = useRouter();

  return (
    <Card
      bgColor={bgColor}
      className={className}
      href={url}
      locale={locale}
    >
      <Title>{title}</Title>

      <ImageWrapper>
        <StyledImage
          alt={title}
          height={179}
          layout={'responsive'}
          src={image}
          style={{ objectFit: 'contain' }}
          width={239}
        />
      </ImageWrapper>
    </Card>
  );
};

/**
 * Default props.
 */

CarouselSquareCard.defaultProps = {
  bgColor: amtrolAlfaTheme.colors.grey200
};

/**
 * Export `CarouselSquareCard` component.
 */

export default CarouselSquareCard;
